/**
 * DOM → SVG vectoriser.
 *
 * The usual html-to-image style export wraps the whole DOM in a single
 * <foreignObject>, which Figma imports as one flat picture. This walks the tree
 * instead and emits primitives Figma understands natively:
 *
 *   background  → <rect>   → Figma rectangle
 *   border      → <rect> / <line> → Figma rectangle / line
 *   text        → <text>   → Figma text layer, one per rendered line
 *   inline svg  → copied through → Figma vector layers
 *   <img>       → <image>  → Figma image fill
 *
 * The result is fully editable after pasting: real text you can retype, real
 * shapes you can restyle. Effects CSS can express but SVG cannot map cleanly
 * (box-shadow, backdrop blur, background images) are intentionally dropped
 * rather than faked.
 */

const TRANSPARENT = /^(transparent|rgba\(0,\s*0,\s*0,\s*0\))$/;
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const num = (v: string) => parseFloat(v) || 0;
const round = (n: number) => Math.round(n * 100) / 100;
type Ctx = {
  ox: number;
  oy: number;
  out: string[];
};

/** Emitted only when the paint is actually visible. */
function paintable(color: string) {
  return !!color && !TRANSPARENT.test(color.replace(/\s/g, ''));
}
function emitBox(el: Element, cs: CSSStyleDeclaration, r: DOMRect, ctx: Ctx) {
  const x = round(r.left - ctx.ox);
  const y = round(r.top - ctx.oy);
  const w = round(r.width);
  const h = round(r.height);
  if (w <= 0 || h <= 0) return;
  const radius = round(num(cs.borderTopLeftRadius));
  const rx = radius ? ` rx="${Math.min(radius, w / 2)}"` : '';
  if (paintable(cs.backgroundColor)) {
    ctx.out.push(`<rect x="${x}" y="${y}" width="${w}" height="${h}"${rx} fill="${cs.backgroundColor}"/>`);
  }

  // Borders. A uniform border becomes one stroked rect; asymmetric borders —
  // the hairline dividers used all over these screens — become single lines,
  // which is what they actually are.
  const sides = [['Top', cs.borderTopWidth, cs.borderTopColor], ['Right', cs.borderRightWidth, cs.borderRightColor], ['Bottom', cs.borderBottomWidth, cs.borderBottomColor], ['Left', cs.borderLeftWidth, cs.borderLeftColor]] as const;
  const widths = sides.map(s => num(s[1]));
  const uniform = widths.every(v => v === widths[0]) && widths[0] > 0 && sides.every(s => s[2] === cs.borderTopColor);
  if (uniform && paintable(cs.borderTopColor)) {
    const o = widths[0] / 2;
    ctx.out.push(`<rect x="${round(x + o)}" y="${round(y + o)}" width="${round(w - widths[0])}" height="${round(h - widths[0])}"${rx} fill="none" stroke="${cs.borderTopColor}" stroke-width="${widths[0]}"/>`);
  } else {
    sides.forEach(([side, width, color], i) => {
      const bw = widths[i];
      if (!bw || !paintable(color)) return;
      const o = bw / 2;
      const line = side === 'Top' ? [x, y + o, x + w, y + o] : side === 'Bottom' ? [x, y + h - o, x + w, y + h - o] : side === 'Left' ? [x + o, y, x + o, y + h] : [x + w - o, y, x + w - o, y + h];
      ctx.out.push(`<line x1="${round(line[0])}" y1="${round(line[1])}" x2="${round(line[2])}" y2="${round(line[3])}" stroke="${color}" stroke-width="${bw}"/>`);
    });
  }
}

/**
 * Text, one <text> per rendered line. Measuring word by word and grouping by
 * line box is what keeps wrapped paragraphs as separate editable lines instead
 * of one run that Figma re-wraps differently.
 */
function emitText(node: Text, cs: CSSStyleDeclaration, ctx: Ctx) {
  const raw = node.textContent ?? '';
  if (!raw.trim()) return;
  const size = num(cs.fontSize);
  const lines = new Map<number, {
    left: number;
    right: number;
    top: number;
    height: number;
    words: string[];
  }>();
  const range = document.createRange();
  const re = /\S+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw))) {
    range.setStart(node, m.index);
    range.setEnd(node, m.index + m[0].length);
    const rects = range.getClientRects();
    if (!rects.length) continue;
    const b = rects[0];
    const key = Math.round(b.top);
    const line = lines.get(key);
    if (line) {
      line.left = Math.min(line.left, b.left);
      line.right = Math.max(line.right, b.right);
      line.words.push(m[0]);
    } else {
      lines.set(key, {
        left: b.left,
        right: b.right,
        top: b.top,
        height: b.height,
        words: [m[0]]
      });
    }
  }
  range.detach?.();
  const align = cs.textAlign;
  const spacing = cs.letterSpacing === 'normal' ? '' : ` letter-spacing="${num(cs.letterSpacing)}"`;
  // Quote each family so Figma matches the name rather than the whole stack.
  const family = cs.fontFamily.split(',')[0].replace(/["']/g, '');
  for (const line of lines.values()) {
    // Alphabetic baseline sits roughly 80% of the em down from the cap line;
    // centring that within the line box lands text where the browser drew it.
    const baseline = line.top - ctx.oy + (line.height - size) / 2 + size * 0.8;
    const anchor = align === 'center' ? 'middle' : align === 'right' || align === 'end' ? 'end' : 'start';
    const x = anchor === 'middle' ? (line.left + line.right) / 2 : anchor === 'end' ? line.right : line.left;
    ctx.out.push(`<text x="${round(x - ctx.ox)}" y="${round(baseline)}" font-family="${esc(family)}" font-size="${round(size)}" font-weight="${cs.fontWeight}" fill="${cs.color}"${spacing} text-anchor="${anchor}" xml:space="preserve">${esc(line.words.join(' '))}</text>`);
  }
}
function walk(el: Element, ctx: Ctx) {
  const cs = getComputedStyle(el);
  if (cs.display === 'none' || cs.visibility === 'hidden' || num(cs.opacity) === 0) return;
  const r = el.getBoundingClientRect();
  // Inline SVG (every icon on these screens) is already vector — copy it in
  // place rather than trying to re-describe it.
  if (el instanceof SVGSVGElement) {
    const clone = el.cloneNode(true) as SVGSVGElement;
    clone.removeAttribute('class');
    if (!clone.getAttribute('width')) clone.setAttribute('width', String(round(r.width)));
    if (!clone.getAttribute('height')) clone.setAttribute('height', String(round(r.height)));
    // Lucide strokes inherit currentColor, which means nothing once detached.
    if (clone.getAttribute('stroke') === 'currentColor') clone.setAttribute('stroke', cs.color);
    if (clone.getAttribute('fill') === 'currentColor') clone.setAttribute('fill', cs.color);
    ctx.out.push(`<g transform="translate(${round(r.left - ctx.ox)},${round(r.top - ctx.oy)})">${clone.outerHTML}</g>`);
    return;
  }
  emitBox(el, cs, r, ctx);
  if (el instanceof HTMLImageElement && el.currentSrc) {
    ctx.out.push(`<image href="${esc(el.currentSrc)}" x="${round(r.left - ctx.ox)}" y="${round(r.top - ctx.oy)}" width="${round(r.width)}" height="${round(r.height)}" preserveAspectRatio="xMidYMid meet"/>`);
    return;
  }
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      emitText(child as Text, cs, ctx);
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      walk(child as Element, ctx);
    }
  }
}

/** Serialise an on-screen element to editable SVG markup. */
export function domToSvg(root: HTMLElement, background = '#FFFFFF'): string {
  const r = root.getBoundingClientRect();
  const ctx: Ctx = {
    ox: r.left,
    oy: r.top,
    out: []
  };
  walk(root, ctx);
  const w = round(r.width);
  const h = round(r.height);
  return [`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">`, `<rect width="${w}" height="${h}" fill="${background}"/>`, ...ctx.out, '</svg>'].join('');
}
